import React, { useEffect, useState } from 'react';
import { researchToolsApi } from '../api';
import type { AnalysisFileItem, DirectionCatalogItem } from '../types';
import '../research-tools-overlay.css';

interface Props {
  onBack: () => void;
}

/** Data analysis — template analysis() with research-tools-* classes. */
export const DataAnalysisView: React.FC<Props> = ({ onBack }) => {
  const [catalog, setCatalog] = useState<DirectionCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDirectionId, setActiveDirectionId] = useState<number>(1);
  const [selectedToolId, setSelectedToolId] = useState<string>('AUTO');
  const [files, setFiles] = useState<AnalysisFileItem[]>([]);
  const [goal, setGoal] = useState('');
  const [resultFormat, setResultFormat] = useState('Отчёт + таблицы + визуализации');
  const [editableSources, setEditableSources] = useState('Да');
  const [submitting, setSubmitting] = useState(false);
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);

  useEffect(() => {
    researchToolsApi
      .getCatalog()
      .then((res) => {
        setCatalog(res);
        if (res.length > 0) setActiveDirectionId(res[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeDirection = catalog.find((d) => d.id === activeDirectionId);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const added: AnalysisFileItem[] = Array.from(e.target.files).map((f) => ({
        name: f.name,
        size: f.size,
      }));
      setFiles((prev) => [...prev, ...added]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) {
      alert('Ошибка валидации: Необходимо прикрепить хотя бы один файл данных для анализа!');
      return;
    }
    if (!goal.trim()) {
      alert('Укажите исследовательскую задачу или исследовательский вопрос');
      return;
    }

    let toolName = 'Система подберёт автоматически';
    if (selectedToolId !== 'AUTO' && activeDirection) {
      const found = activeDirection.tools.find((t) => t.id === selectedToolId);
      if (found) toolName = found.name;
    }

    setSubmitting(true);
    try {
      const res = await researchToolsApi.createAnalysisTask({
        direction_id: activeDirectionId,
        direction_name: activeDirection ? activeDirection.name : 'Статистический анализ',
        tool_name: toolName,
        files,
        goal,
        result_format: resultFormat,
        editable_sources: editableSources,
      });
      setCreatedTaskId(res.id);
      alert('Задача анализа данных успешно создана и передана на обработку');
    } catch {
      alert('Ошибка при создании задачи анализа');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="research-tools-panel">
        <div className="research-tools-panel-body" style={{ textAlign: 'center', padding: '2rem' }}>
          Загрузка каталога инструментов…
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="research-tools-hero">
        <div>
          <small>Zertteu Academy · исследовательский контур</small>
          <h2>Анализ данных через программы</h2>
          <p>
            Сначала выберите направление, затем один инструмент. Материал для анализа обязателен;
            система может подобрать инструмент автоматически.
          </p>
        </div>
        <button type="button" className="research-tools-btn" onClick={onBack}>
          ← Назад
        </button>
      </section>

      <section className="research-tools-panel">
        <header>
          <div>
            <h3>1. Направление анализа</h3>
            <p>Шесть групп из утверждённого набора инструментов.</p>
          </div>
        </header>
        <div className="research-tools-panel-body">
          <div
            className="research-tools-action-grid"
            style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginTop: 0 }}
          >
            {catalog.map((dir) => {
              const selected = activeDirectionId === dir.id;
              return (
                <button
                  key={dir.id}
                  type="button"
                  className="research-tools-action"
                  style={{
                    ['--rt-accent' as string]: selected ? '#e2ad3a' : '#41d2ff',
                    minHeight: 130,
                  }}
                  onClick={() => {
                    setActiveDirectionId(dir.id);
                    setSelectedToolId('AUTO');
                  }}
                >
                  <h3>
                    {dir.id}. {dir.name}
                  </h3>
                  <p>{dir.tools.length} инструментов</p>
                  <b>{selected ? 'Выбрано' : 'Выбрать →'}</b>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {activeDirection ? (
        <section className="research-tools-panel">
          <header>
            <div>
              <h3>2. Инструмент · {activeDirection.name}</h3>
              <p>Выберите одну программу / библиотеку или автоматический подбор.</p>
            </div>
          </header>
          <div className="research-tools-panel-body">
            <div className="research-tools-tool-picker">
              <label className="research-tools-auto-tool">
                <input
                  type="radio"
                  name="analysis_tool"
                  checked={selectedToolId === 'AUTO'}
                  onChange={() => setSelectedToolId('AUTO')}
                />
                <span>
                  <strong>Система подберёт автоматически</strong>
                  <small>Выбор по формату данных, методу и требуемому результату.</small>
                </span>
              </label>
              <div className="research-tools-tool-options">
                {activeDirection.tools.map((tool) => (
                  <label
                    key={tool.id}
                    className={`research-tools-tool-option${
                      selectedToolId === tool.id ? ' selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="analysis_tool"
                      checked={selectedToolId === tool.id}
                      onChange={() => setSelectedToolId(tool.id)}
                    />
                    <span>
                      <strong>{tool.name}</strong>
                      <small>{tool.purpose}</small>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="research-tools-panel">
        <header>
          <div>
            <h3>3. Данные и задача</h3>
            <p>Исходный материал обязателен.</p>
          </div>
        </header>
        <form className="research-tools-panel-body" onSubmit={handleSubmit}>
          <div className="research-tools-form">
            <div className="research-tools-field full">
              <label>Загрузите данные / материал *</label>
              <input type="file" multiple onChange={handleFileUpload} />
              {files.length > 0 ? (
                <small style={{ color: '#89a7b7' }}>
                  Прикреплено: {files.map((f) => f.name).join(', ')}
                </small>
              ) : null}
            </div>
            <div className="research-tools-field full">
              <label>Что нужно выяснить?</label>
              <textarea
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Исследовательский вопрос, гипотеза или ожидаемый анализ"
                rows={4}
              />
            </div>
            <div className="research-tools-field">
              <label>Формат результата</label>
              <select value={resultFormat} onChange={(e) => setResultFormat(e.target.value)}>
                <option>Отчёт + таблицы + визуализации</option>
                <option>Только статистический / аналитический отчёт</option>
                <option>Таблицы и графики</option>
                <option>Файлы для статьи</option>
              </select>
            </div>
            <div className="research-tools-field">
              <label>Нужны редактируемые исходники?</label>
              <select value={editableSources} onChange={(e) => setEditableSources(e.target.value)}>
                <option value="Да">Да</option>
                <option value="Нет">Нет</option>
                <option value="Определить автоматически">Определить автоматически</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="submit" className="research-tools-btn primary" disabled={submitting}>
              {submitting ? 'Формирование задачи…' : 'Сформировать задачу анализа'}
            </button>
          </div>
          {createdTaskId ? (
            <p style={{ marginTop: 12, color: '#49ddb7', fontWeight: 700 }}>
              Задача зарегистрирована · {createdTaskId}
            </p>
          ) : null}
        </form>
      </section>
    </div>
  );
};
